import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, OAuthProvider, RecaptchaVerifier, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, set, get } from "firebase/database";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { useToastStore } from "store/toastStore";
import { getFirestore, doc, setDoc, addDoc, collection, getDocs, getDoc, onSnapshot } from "firebase/firestore";
import { Timestamp } from "firebase-admin/firestore";
import firebase from 'firebase/app';
import { writeBatch } from "firebase/firestore";


interface Notification {
  id: string;
  IsRead: boolean;
  createdAt: Timestamp;
  message: string;
  requester: string;
  responder: string;
  title: string;
}




// Firebase web SDK configuration for the `standbytravel-1e2f1` project.
// These values are NOT secrets — Firebase web config is meant to ship in the
// client bundle (access is gated by Firebase Security Rules, not by hiding this
// config). Keeping it here makes the code the single source of truth, so the
// app can't silently point at the wrong project via a mis-set env var.
const firebaseConfig = {
  apiKey: "AIzaSyDWfQ7xY592EGsmIfUd2YIcNwNIh6GRAhA",
  authDomain: "standbytravel-1e2f1.firebaseapp.com",
  projectId: "standbytravel-1e2f1",
  storageBucket: "standbytravel-1e2f1.firebasestorage.app",
  messagingSenderId: "1058374584279",
  appId: "1:1058374584279:web:c95fb82009080ceca0ed92",
  measurementId: "G-SYDDT0HVT3",
  databaseURL: "https://standbytravel-1e2f1-default-rtdb.firebaseio.com/"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Firebase Cloud Messaging requires the Service Worker API, which browsers only
// expose on localhost or HTTPS. Over a plain-HTTP LAN origin (e.g. testing on a
// phone via http://192.168.x.x:3000) `navigator.serviceWorker` is undefined and
// getMessaging() throws, crashing the whole app at module load. Guard it so the
// app still loads; push notifications simply won't be available in that context.
const isMessagingSupported =
  typeof navigator !== "undefined" && "serviceWorker" in navigator;

let messaging: ReturnType<typeof getMessaging> | null = null;
if (isMessagingSupported) {
  try {
    messaging = getMessaging(app);
  } catch (err) {
    console.warn("Firebase Messaging is not available in this context:", err);
  }
}
const auth = getAuth(app);
const database = getDatabase(app);
const firestore = getFirestore(app);
const appleProvider = new OAuthProvider("apple.com");
// Force Apple's sign-in page to English (otherwise it follows the browser/device language)
appleProvider.setCustomParameters({ locale: "en" });
const googleProvider = new GoogleAuthProvider();

// ✅ Function to Get the Current User's UID
const getUserId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        resolve(user.uid);
      } else {
        resolve(null);
      }
    });
  });
};

// ✅ Function to Save User Settings
const saveSettings = (userId: string, settings: object) => {
  const settingsRef = ref(database, 'users/' + userId + '/settings');

  return new Promise<void>((resolve, reject) => {
    set(settingsRef, settings)
      .then(() => {
        resolve(); // Resolve when saved successfully
      })
      .catch((error) => {
        reject(error); // Reject on error
      });
  });
};

// ✅ Function to Get User Settings
const getSettings = async (userId: string) => {
  const settingsRef = ref(database, 'users/' + userId + '/settings');
  const snapshot = await get(settingsRef);
  if (snapshot.exists()) {
    return snapshot.val();
  } else {
    return null; // No settings found
  }
};

// Function to request notification permission and get the device token
const logDeviceToken = async () => {
  try {
    if (!messaging) {
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });

    // Display token on screen if needed
    const displayElement = document.getElementById("token-display");
    if (displayElement) {
      displayElement.innerText = token;
    }

    // Save token to Firestore if user is authenticated
    const user = auth.currentUser;
    if (user && token) {
      const tokenRef = doc(firestore, "deviceTokens", user.uid);
      await setDoc(tokenRef, { token });
    }

    return token;
  } catch (err) {
    console.error("Error getting or saving device token:", err);
    return null;
  }
};


// ✅ New function to handle FCM messages with toast notifications
const setupMessageHandler = () => {
  if (!messaging) {
    return () => {}; // no-op unsubscribe
  }

  const { showToast } = useToastStore.getState(); // Get the function directly from the store

  return onMessage(messaging, (payload) => {
    // Extract notification data
    const title = payload.notification?.title || 'New Notification';
    const message = payload.notification?.body || '';

    // Determine notification type based on payload if available
    let type = "info";
    if (payload.data && payload.data.type) {
      if (payload.data.type === "success" || payload.data.type === "error") {
        type = payload.data.type;
      }
    }

    // Show toast notification using your Zustand store
    showToast(message, type as "success" | "error" | "info", 5000);
  });
};


export const requestPermissionAndSaveToken = async () => {
  try {
    if (!messaging) {
      return;
    }
    const token = await getToken(messaging, {
      vapidKey: "BO16_PQwa4xYENvbnBNaDXlBCJBaV0xclX4VmRgPG0fabMlmF-Qc9qpAe2f_ZdG5gP3poW526ZagqINSk9W2Je4", // replace with your actual VAPID key
    });

    if (token) {
      const user = auth.currentUser;
      if (!user) return;

      // Save using UID as document ID to avoid duplicates
      await setDoc(doc(firestore, "deviceTokens", user.uid), {
        uid: user.uid,
        token: token,
        updatedAt: new Date(),
      });
    } else {
      console.warn("⚠️ No token received, permission might be denied.");
    }
  } catch (error) {
    console.error("❌ Error getting FCM token or saving to Firestore:", error);
  }
};




const fetchUserNotifications = async (): Promise<Notification[] | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    const uid = user.uid;

    const notificationsCollectionRef = collection(
      firestore,
      "load_notifications",
      uid,
      "notifications"
    );

    const querySnapshot = await getDocs(notificationsCollectionRef);

    if (!querySnapshot.empty) {
      const notifications: Notification[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          IsRead: data.IsRead,
          createdAt: data.createdAt,
          message: data.message,
          requester: data.requester,
          responder: data.responder,
          responderName: data.responderName,
          title: data.title,
        };
      });

      return notifications;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};


export const updateNotificationAsRead = async (notificationId: string, userId: string) => {
  try {
    // Create the reference to the specific notification document by its ID
    const docRef = doc(firestore, 'load_notifications', userId, 'notifications', notificationId);

    // Get the document snapshot
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, count: 0 };
    }

    // Prepare a batch for updating notifications
    const batch = writeBatch(firestore);

    // Update the IsRead field for the specific notification document
    batch.update(docRef, { IsRead: true });

    // Commit the batch update
    await batch.commit();

    return { success: true, count: 1 };
  } catch (error) {
    console.error("Error updating notification:", error);
    return { success: false, error };
  }
};

export const subscribeToUserNotifications = (
  userId: string,
  callback: (notifications: any[]) => void
) => {
  const notificationsRef = collection(firestore, "load_notifications", userId, "notifications");

  // No orderBy("createdAt") here: Firestore treats orderBy as a filter and
  // silently drops any document that is missing that field, which can hide
  // notifications entirely. Consumers sort client-side, so order it there.
  return onSnapshot(
    notificationsRef,
    (snapshot) => {
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(notifications);
    },
    (error) => {
      // Surface permission/index errors instead of failing silently.
      console.error("Error subscribing to notifications:", error);
      callback([]);
    },
  );
};

// Export everything
export {
  app,
  // analytics,
  auth,
  database,
  GoogleAuthProvider,
  googleProvider,
  appleProvider,
  signInWithPopup,
  RecaptchaVerifier,
  getUserId,
  messaging,
  saveSettings,
  getSettings,
  logDeviceToken,
  firestore,
  setupMessageHandler, // Export the new function
  fetchUserNotifications
};